import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { BlocksElement } from '../BlocksElement';
import type { LayoutBreakVariant, LayoutGapVariant, LayoutStyleVariant } from './layout.model';
import styles from './layout.styles';

/**
 * @slot default - The layout content
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 */
export class BlocksLayout extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Break variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li> **faster** breaks the grid at a smaller width than the default.
   * Example: Sidebar breaks underneath main layout section at smaller breakpoint than default</li>
   * <li> **slower** breaks the grid at a larger width than the default.
   * Example: Sidebar breaks underneath main layout section at wider breakpoint than default</li>
   * <li> **none** the grid does not break.</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  break: LayoutBreakVariant;

  /**
   * Gap variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>The default spacing is 16px
   * <li>**none** yields a layout whose layout sections are spaced without any gutter in between on large screens.
   * Small screens gap sits at 16px.</li>
   * <li>**sm** yields a layout whose layout sections are spaced 8px apart on large screens. Small screens gutters move to 16px between.</li>
   * <li>**lg** yields a layout whose layout sections are spaced 24px apart on large screens. Small screens gutters move to 16px between.</li>
   * <li>**xl** yields a layout whose layout sections are spaced 32px apart on large screens. Small screens gutters move to 16px between.</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  gap: LayoutGapVariant;

  /**
   *  Style variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**left-sidebar** formats the first `layout-section` component as a left sidebar</li>
   * <li>**right-sidebar** formats the second `layout-section` component as a right sidebar</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: LayoutStyleVariant;

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-layout', {
      'cat-c-layout--left-sidebar': this.variant === 'left-sidebar',
      'cat-c-layout--right-sidebar': this.variant === 'right-sidebar',
      'cat-c-layout--gap-none': this.gap === 'none',
      'cat-c-layout--gap-sm': this.gap === 'sm',
      'cat-c-layout--gap-lg': this.gap === 'lg',
      'cat-c-layout--gap-xl': this.gap === 'xl',
      'cat-c-layout--break-faster': this.break === 'faster',
      'cat-c-layout--break-slower': this.break === 'slower'
    });

    return html`
      <div part="base" class="${componentClassName}">
        <slot part="part-default"></slot>
      </div>
    `;
  }
}

if (customElements.get('cat-layout') === undefined) {
  customElements.define('cat-layout', BlocksLayout);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-layout': BlocksLayout;
  }
}
